// Agent learning loop — admin API client (kanban 651f3aa5).
// Backend: app/routes/feedback_learning.py (GET/POST /api/admin/feedback-learning/...).
// Ledger + curation FSM: db/models/feedback_learning.py.

import api from './api';

export type LearningSignalSource =
  | 'console_feedback'
  | 'edit_proposal'
  | 'alert_outcome'
  | 'kirk_draft_edit'
  | 'order_correction'
  | 'data_request_cancel';

export type LearningSignalStatus =
  | 'pending'
  | 'reflecting'
  | 'written'
  | 'skipped'
  | 'failed'
  | 'superseded';

export type FeedbackCaseStatus = 'proposed' | 'accepted' | 'rejected';

export const LEARNING_SIGNAL_SOURCES: readonly LearningSignalSource[] = [
  'console_feedback',
  'edit_proposal',
  'alert_outcome',
  'kirk_draft_edit',
  'order_correction',
  'data_request_cancel',
];

export const LEARNING_SIGNAL_STATUSES: readonly LearningSignalStatus[] = [
  'pending',
  'reflecting',
  'written',
  'skipped',
  'failed',
  'superseded',
];

export const SOURCE_DISPLAY: Record<LearningSignalSource, string> = {
  console_feedback: 'Console 👎 / comment',
  edit_proposal: 'Merlin edit proposal',
  alert_outcome: 'Audit alert outcome',
  kirk_draft_edit: 'Kirk draft edited',
  order_correction: 'CSR order correction',
  data_request_cancel: 'Data request cancelled',
};

// Legal curation moves, mirroring ALLOWED_CASE_TRANSITIONS on the backend
// (proposed -> accepted | rejected; a verdict can be flipped or reopened).
export const CASE_TRANSITIONS: Record<FeedbackCaseStatus, readonly FeedbackCaseStatus[]> = {
  proposed: ['accepted', 'rejected'],
  accepted: ['rejected', 'proposed'],
  rejected: ['accepted', 'proposed'],
};

/** One KB write the reflecting agent made (contracts.KbWrite). */
export interface KbWrite {
  tool: string;
  bare_tool: string;
  mount: string | null;
  input: Record<string, unknown>;
}

export interface HumanQuestion {
  question: string;
  context?: string | null;
}

export interface LedgerSummary {
  id: string;
  source: LearningSignalSource;
  source_ref: string;
  source_version: string;
  conversation_id: string | null;
  profile_id: string | null;
  model: string | null;
  status: LearningSignalStatus;
  attempts: number;
  kb_writes: KbWrite[] | null;
  questions: HumanQuestion[] | null;
  eval_case_id: string | null;
  cost_usd: string | number | null;
  error: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

/** The normalised signal the pass consumed (contracts.LearningSignal). */
export interface LearningSignalPayload {
  headline?: string;
  verdict?: string;
  human_notes?: string | null;
  actor?: string | null;
  occurred_at?: string;
  kind?: string;
  before?: Record<string, unknown> | null;
  after?: Record<string, unknown> | null;
  current_state?: Record<string, unknown> | null;
  audit_trail?: { at?: string | null; actor?: string | null; what: string; before?: unknown; after?: unknown }[];
  links?: Record<string, string>;
  [key: string]: unknown;
}

/** One tool call of the reflection turn (channels.turn_client.ToolUseRecord). */
export interface TurnToolUse {
  tool_use_id?: string | null;
  name: string;
  input: Record<string, unknown>;
  is_error: boolean;
}

/** The whole ConversationTurnResult, stored verbatim on the ledger row. */
export interface TurnResultPayload {
  text?: string;
  model?: string | null;
  cost_usd?: number | null;
  input_tokens?: number | null;
  output_tokens?: number | null;
  tool_calls?: number;
  tool_uses?: TurnToolUse[];
  error?: string | null;
  timed_out?: boolean;
  kb_calls?: number;
  kb_failures?: number;
  [key: string]: unknown;
}

export interface LedgerDetail extends LedgerSummary {
  signal: LearningSignalPayload;
  reflection_prompt: string | null;
  turn_result: TurnResultPayload | null;
  assistant_message_id: string | null;
  question_delivery: Record<string, unknown> | null;
  input_tokens: number | null;
  output_tokens: number | null;
}

export interface FeedbackCase {
  id: string;
  lesson_id: string;
  agent_type: string;
  case_id: string;
  intent_family: string;
  case: Record<string, unknown>;
  status: FeedbackCaseStatus;
  curated_by: string | null;
  curated_at: string | null;
  curation_notes: string | null;
  created_at: string;
}

export interface LoopStats {
  by_status: Record<string, number>;
  by_source: Record<string, number>;
  kb_writes: number;
  questions: number;
  eval_cases_by_status: Record<string, number>;
  total_cost_usd: string | number;
}

export interface RunQueued {
  task_id: string;
  queued: boolean;
}

const BASE = '/admin/feedback-learning';

export const feedbackLearningApi = {
  stats: async (): Promise<LoopStats> => {
    const r = await api.get<LoopStats>(`${BASE}/stats`);
    return r.data;
  },

  listLessons: async (params?: {
    status?: LearningSignalStatus;
    source?: LearningSignalSource;
    profile_id?: string;
    limit?: number;
    offset?: number;
  }): Promise<LedgerSummary[]> => {
    const r = await api.get<LedgerSummary[]>(`${BASE}/lessons`, { params });
    return r.data;
  },

  getLesson: async (lessonId: string): Promise<LedgerDetail> => {
    const r = await api.get<LedgerDetail>(`${BASE}/lessons/${lessonId}`);
    return r.data;
  },

  listCases: async (params?: {
    agent_type?: string;
    status?: FeedbackCaseStatus;
    limit?: number;
    offset?: number;
  }): Promise<FeedbackCase[]> => {
    const r = await api.get<FeedbackCase[]>(`${BASE}/cases`, { params });
    return r.data;
  },

  curateCase: async (
    caseId: string,
    status: FeedbackCaseStatus,
    notes?: string,
  ): Promise<FeedbackCase> => {
    const r = await api.post<FeedbackCase>(`${BASE}/cases/${caseId}/curate`, {
      status,
      notes: notes ?? null,
    });
    return r.data;
  },

  runNow: async (): Promise<RunQueued> => {
    const r = await api.post<RunQueued>(`${BASE}/run`);
    return r.data;
  },
};
